import { CheckOutlined, CloseOutlined, EditOutlined, EyeOutlined, PrinterOutlined } from "@ant-design/icons";
import { Button, Modal, notification, Space, Tag, Typography } from "antd";
import {
  IOU_ADJUSTMENT_LIST,
  IOU_ADJUSTMENT_PDF_ENDPOINT,
  IOU_ADJUSTMENT_REQUESTED_BY_USER_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IOU_ADJUSTMENT_IMPORT_APPROVE_PERMISSION,
  IOU_ADJUSTMENT_IMPORT_CREATE_PERMISSION,
  IOU_ADJUSTMENT_IMPORT_EDIT_PERMISSION,
  IOU_ADJUSTMENT_IMPORT_VIEW_PERMISSION,
} from "routes/permissions";
import { cglFormatDate, formatNumberWithCommasFloat, hasPermission } from "utils/functions";
import { checkPermission } from "./config/checkPermission";
import { STATUS_LABELS } from "./constants/status";

const STATUS_COLORS = {
  created: "default",
  requested: "processing",
  by_department: "warning",
  by_director: "gold",
  approved: "success",
  rejected: "error",
};

export default function IOUAdjustmentImport() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;
  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);
  const loggedInUserRole = localStorage.getItem("user_type");

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);

  const { Title } = Typography;
  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    localStorage.setItem(
      "filter_status",
      JSON.stringify({
        ...parsedFilterStatus,
        [routerPath]: !!showFilter,
      }),
    );
  }, [showFilter, routerPath]);

  const getAllIOUAdjustments = useCallback(
    async (filterValues) => {
      const { iou_number, status } = filterValues || {};
      setLoading(true);
      setFetchError(null);

      const query = `${IOU_ADJUSTMENT_LIST}?page=${currentPage}`;
      const bodyData = {
        adjustment_for: "import",
        ...(iou_number && { iou_number }),
        ...(status && { status }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        setTableData(
          data.map((item, index) => ({
            ...item,
            key: item.id || `iou-adj-import-${index}`,
          })),
        );
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
      } else {
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage],
  );

  useEffect(() => {
    getAllIOUAdjustments(filterData);
  }, [refresh, filterData, getAllIOUAdjustments]);

  const refetch = useCallback(() => setRefresh((prev) => prev + 1), []);

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/iou-adjustment-for-import/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-adjustment-for-import/edit/${record.id}`);
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-adjustment-for-import/view/${record.id}`);
    },
    [navigate],
  );

  const changePage = (newPage) => setCurrentPage(newPage);

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);

    const allSameStatus =
      newSelectedRows.length > 0 && newSelectedRows.every((row) => row.status === newSelectedRows[0].status);

    if (allSameStatus) {
      const hasApproveKey = hasPermission([IOU_ADJUSTMENT_IMPORT_APPROVE_PERMISSION]);
      setCanRequest(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.entry_by || loggedInUserId === row?.user?.id,
            "request",
            row?.department_approve,
            row?.director_approve,
            row?.account_approve,
          ),
        ),
      );
      setCanApprove(
        hasApproveKey &&
          newSelectedRows.some((row) =>
            checkPermission(
              loggedInUserRole,
              row.status,
              loggedInUserId === row?.entry_by || loggedInUserId === row?.user?.id,
              "approve",
              row?.department_approve,
              row?.director_approve,
              row?.account_approve,
            ),
          ),
      );
      setCanReject(
        hasApproveKey &&
          newSelectedRows.some((row) =>
            checkPermission(
              loggedInUserRole,
              row.status,
              loggedInUserId === row?.entry_by || loggedInUserId === row?.user?.id,
              "reject",
              row?.department_approve,
              row?.director_approve,
              row?.account_approve,
            ),
          ),
      );
    } else {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleRequestAction = async (val) => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Warning",
        description: "No rows selected",
        placement: "bottomRight",
      });
      return;
    }

    const actionLabel = val === "approved" ? "Approve" : val === "rejected" ? "Reject" : "Request";

    Modal.confirm({
      title: `Confirm ${actionLabel}`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()} the selected IOU Adjustment(s)?`,
      okText: `Yes, ${actionLabel}`,
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const formData = new FormData();
          formData.append(
            "requested_id",
            JSON.stringify(selectedRows.map((row) => row.id)),
          );
          formData.append("status", val);

          const response = await postData(IOU_ADJUSTMENT_REQUESTED_BY_USER_ENDPOINT, formData);
          if (response && (response?.code === 200 || response?.status === 200)) {
            alertPop("success", response.message || `${actionLabel} successful`);
            setSelectedRowKeys([]);
            setSelectedRows([]);
            refetch();
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: `Failed to ${actionLabel.toLowerCase()} IOU Adjustment`,
            placement: "bottomRight",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleDownloadAction = async (record) => {
    try {
      setLoading(true);
      const response = await getData(
        `${IOU_ADJUSTMENT_PDF_ENDPOINT}/${record.id}`,
        false,
        null,
        null,
        "arraybuffer",
      );
      const data = response?.data;
      const header =
        data && data.byteLength >= 4
          ? String.fromCharCode(...new Uint8Array(data.slice(0, 4)))
          : "";

      if (response?.status === 200 && header === "%PDF") {
        const iouNo = record?.iou_info?.iou_number || record.id;
        const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `miscellaneous_bill_import_${iouNo}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        HandleNotification("error", "bottomRight", "Error getting PDF report!", null);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to download Miscellaneous Bill PDF",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "IOU Number",
        dataIndex: ["iou_info", "iou_number"],
        width: "12%",
        key: "iou_number",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: "14%",
        render: (status, record) => {
          let label = STATUS_LABELS[status] || status || "-";
          if (status === "rejected" && record.department_approve === "rejected") {
            label = "Rejected by Department Head";
          } else if (status === "rejected" && record.director_approve === "rejected") {
            label = "Rejected by Director";
          } else if (status === "rejected" && record.account_approve === "rejected") {
            label = "Rejected by F&A";
          }
          return <Tag color={STATUS_COLORS[status] || "default"}>{label}</Tag>;
        },
      },
      {
        title: "Approved RA",
        dataIndex: "approved_ra",
        key: "approved_ra",
        width: "9%",
        render: (text) => formatNumberWithCommasFloat(text),
      },
      {
        title: "Approved NRA",
        dataIndex: "approved_nra",
        key: "approved_nra",
        width: "9%",
        render: (text) => formatNumberWithCommasFloat(text),
      },
      {
        title: "Total Approved",
        dataIndex: "total_approve_amount",
        key: "total_approve_amount",
        width: "10%",
        render: (text) => formatNumberWithCommasFloat(text),
      },
      {
        title: "Bill Amount RA",
        dataIndex: "bill_amount_ra",
        key: "bill_amount_ra",
        width: "9%",
        render: (text) => formatNumberWithCommasFloat(text),
      },
      {
        title: "Bill Amount NRA",
        dataIndex: "bill_amount_nra",
        key: "bill_amount_nra",
        width: "9%",
        render: (text) => formatNumberWithCommasFloat(text),
      },
      {
        title: "Total Bill",
        dataIndex: "total_bill_amount",
        key: "total_bill_amount",
        width: "9%",
        render: (text) => formatNumberWithCommasFloat(text),
      },
      {
        title: "Request Date",
        dataIndex: "request_date",
        key: "request_date",
        width: "9%",
        render: (text) => cglFormatDate(text),
      },
      {
        title: "Requested By",
        dataIndex: ["user", "name"],
        key: "requested_by",
        width: "10%",
        render: (_, record) => record.user?.name || "-",
      },
      {
        title: "Action",
        key: "action",
        width: "16%",
        fixed: "right",
        render: (_, record) => {
          const isCreator =
            loggedInUserId === record?.entry_by || loggedInUserId === record?.user?.id;
          const canView = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "view",
            record?.department_approve,
            record?.director_approve,
            record?.account_approve,
          );
          const canUpdate =
            hasPermission([IOU_ADJUSTMENT_IMPORT_EDIT_PERMISSION]) &&
            checkPermission(
              loggedInUserRole,
              record.status,
              isCreator,
              "update",
              record?.department_approve,
              record?.director_approve,
              record?.account_approve,
            );

          return (
            <Space>
              {canView && hasPermission([IOU_ADJUSTMENT_IMPORT_VIEW_PERMISSION]) && (
                <Button type="default" icon={<EyeOutlined />} onClick={() => navigateToView(record)} size="small">
                  View
                </Button>
              )}
              {canUpdate && (
                <Button type="primary" icon={<EditOutlined />} onClick={() => navigateToEdit(record)} size="small">
                  Edit
                </Button>
              )}
              <Button
                type="default"
                icon={<PrinterOutlined />}
                onClick={() => handleDownloadAction(record)}
                size="small"
              >
                Print
              </Button>
            </Space>
          );
        },
      },
    ],
    [navigateToView, navigateToEdit, loggedInUserId, loggedInUserRole],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllIOUAdjustments}
        filterName="iou_cost"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllIOUAdjustments],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <div style={{ display: "flex", alignItems: "center" }}>
              {selectedRowKeys.length > 0 && canReject && (
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleRequestAction("rejected")}
                  style={{ marginRight: 10 }}
                >
                  Reject
                </Button>
              )}
              {selectedRowKeys.length > 0 && canRequest && (
                <Button
                  type="primary"
                  onClick={() => handleRequestAction("requested")}
                  style={{
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    marginRight: 10,
                  }}
                >
                  Request
                </Button>
              )}
              {selectedRowKeys.length > 0 && canApprove && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleRequestAction("approved")}
                  style={{
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    marginRight: 10,
                  }}
                >
                  Approve
                </Button>
              )}
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={navigateToAdd}
                disabled={!hasPermission([IOU_ADJUSTMENT_IMPORT_CREATE_PERMISSION])}
                hideAddButton={true}
                addButtonText={null}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
              <Button
                type="primary"
                onClick={navigateToAdd}
                style={{ marginLeft: 8 }}
                disabled={!hasPermission([IOU_ADJUSTMENT_IMPORT_CREATE_PERMISSION])}
              >
                Add New
              </Button>
            </div>
          }
          title={<Title level={5}>IOU Adjustment for Import</Title>}
        />
      }
      content={
        <>
          {memoizedCustomFilter}
          <div className="table" style={{ marginTop: 2 }}>
            <ListTable
              loading={loading}
              error={fetchError}
              onRetry={() => getAllIOUAdjustments(filterData)}
              dataSource={tableData}
              columns={tableColumns}
              rowSelection={rowSelection}
              rowKey="id"
              pagination={{
                showSizeChanger: false,
                defaultPageSize: metaData?.defaultPageSize || 10,
                current: metaData?.current || 1,
                onChange: changePage,
                defaultCurrent: metaData?.defaultCurrent || 1,
                total: metaData?.total || 0,
                showTotal: (total) => `Total ${total} items`,
              }}
              bordered
              size="middle"
              scroll={{
                x: "max-content",
                y: tableData.length > 10 ? 500 : undefined,
              }}
              style={{ borderRadius: "8px", overflow: "hidden" }}
            />
          </div>
        </>
      }
    />
  );
}
